/** 经营总览：同比/环比与月度序列 */

import { resolveMomDeltaDisplay, resolveYoyDeltaDisplay } from '../../../filters/lib/statisticsYoy';
import { resolvePeriodYoyGrid } from '../../../filters/lib/statisticsPeriodYoyHelpers';
import { MONTH_AXIS, QUARTER_AXIS, emptyMonthSeries, sum } from './overviewMetricsFormat';

/** 12 月序列按自然季求和 → 4 点（金额/单数可加总） */
export const aggregateMonthSeriesToQuarter = (values = []) => {
	const months = Array.from({ length: 12 }, (_, index) => Number(values[index]) || 0);
	return [0, 1, 2, 3].map((quarter) => months[quarter * 3] + months[quarter * 3 + 1] + months[quarter * 3 + 2]);
};

/**
 * 当月/当季：横轴两个类目（上年同期 | 本年同期），各落一根柱/一个点。
 * monthIndex 取筛选起止日所在月（0~11），不要用「今天」。
 * @returns {{ axisLabels: string[], priceCurrent: (number|null)[], pricePrevious: (number|null)[], countCurrent: (number|null)[], countPrevious: (number|null)[], legendPriceCurrent: number[], legendPricePrevious: number[], legendCountCurrent: number[], legendCountPrevious: number[] }}
 */
export const buildYoySinglePeriodSeries = ({
	periodUnit = 'year',
	priceCurrent = [],
	pricePrevious = [],
	countCurrent = [],
	countPrevious = [],
	currentYearLabel = '',
	previousYearLabel = '',
	now = new Date(),
	monthIndex: monthIndexProp,
} = {}) => {
	const anchor = now instanceof Date && !Number.isNaN(now.getTime()) ? now : new Date();
	const monthIndex = Number.isFinite(Number(monthIndexProp)) ? Number(monthIndexProp) : anchor.getMonth();
	const currYear = String(currentYearLabel || anchor.getFullYear());
	const prevYear = String(previousYearLabel || anchor.getFullYear() - 1);

	if (periodUnit === 'month') {
		const periodLabel = MONTH_AXIS[monthIndex];
		const currP = Number(priceCurrent?.[monthIndex]) || 0;
		const prevP = Number(pricePrevious?.[monthIndex]) || 0;
		const currC = Number(countCurrent?.[monthIndex]) || 0;
		const prevC = Number(countPrevious?.[monthIndex]) || 0;
		return {
			axisLabels: [`${prevYear}年${periodLabel}`, `${currYear}年${periodLabel}`],
			pricePrevious: [prevP, null],
			priceCurrent: [null, currP],
			countPrevious: [prevC, null],
			countCurrent: [null, currC],
			legendPricePrevious: [prevP],
			legendPriceCurrent: [currP],
			legendCountPrevious: [prevC],
			legendCountCurrent: [currC],
		};
	}
	if (periodUnit === 'quarter') {
		const quarterIndex = Math.floor(monthIndex / 3);
		const periodLabel = QUARTER_AXIS[quarterIndex];
		const currQuarter = aggregateMonthSeriesToQuarter(priceCurrent);
		const prevQuarter = aggregateMonthSeriesToQuarter(pricePrevious);
		const currCountQ = aggregateMonthSeriesToQuarter(countCurrent);
		const prevCountQ = aggregateMonthSeriesToQuarter(countPrevious);
		const currP = Number(currQuarter[quarterIndex]) || 0;
		const prevP = Number(prevQuarter[quarterIndex]) || 0;
		const currC = Number(currCountQ[quarterIndex]) || 0;
		const prevC = Number(prevCountQ[quarterIndex]) || 0;
		return {
			axisLabels: [`${prevYear}年${periodLabel}`, `${currYear}年${periodLabel}`],
			pricePrevious: [prevP, null],
			priceCurrent: [null, currP],
			countPrevious: [prevC, null],
			countCurrent: [null, currC],
			legendPricePrevious: [prevP],
			legendPriceCurrent: [currP],
			legendCountPrevious: [prevC],
			legendCountCurrent: [currC],
		};
	}
	return null;
};

/** KPI 品牌/省份图例色：蓝 / 绿 / 橙区分度更高（避免 primary+info 同冷色难辨） */

const toDeltaTone = (tone) => (tone === 'down' || tone === 'flat' ? tone : 'up');

/** 同比、环比同时返回（横排展示）；不再互相覆盖。getBaseline 优先于 itemKey。 */

/** 同比、环比同时返回（横排展示）；不再互相覆盖。getBaseline 优先于 itemKey。 */
export const resolvePrimaryDeltas = ({ value, mode, itemKey, getBaseline, yoySlotOn, momSlotOn, yoyCompare, momCompare, isYoyCompareLoading, isMomCompareLoading }) => {
	const deltas = [];
	const readBaseline = (compare) => (typeof getBaseline === 'function' ? getBaseline(compare) : compare?.overviewRow?.[itemKey]);

	if (yoySlotOn) {
		if (isYoyCompareLoading) {
			deltas.push({ key: 'yoy', tone: 'up', prefix: '同比', label: '…' });
		} else {
			const display = resolveYoyDeltaDisplay(value, readBaseline(yoyCompare), {
				mode,
				periodLabel: yoyCompare?.headerRangeLabel || '',
				baselineLabel: '去年同期',
				deltaPrefix: yoyCompare?.deltaPrefix ?? '同比',
			});
			if (display) {
				deltas.push({
					key: 'yoy',
					tone: toDeltaTone(display.tone),
					prefix: display.deltaPrefix ?? '同比',
					label: display.label.replace(/^\+/, ''),
				});
			}
		}
	}

	if (momSlotOn) {
		if (isMomCompareLoading) {
			deltas.push({ key: 'mom', tone: 'up', prefix: '环比', label: '…' });
		} else {
			const display = resolveMomDeltaDisplay(value, readBaseline(momCompare), {
				mode,
				periodLabel: momCompare?.headerRangeLabel || '',
				baselineLabel: '上期',
				deltaPrefix: momCompare?.deltaPrefix ?? '环比',
			});
			if (display) {
				deltas.push({
					key: 'mom',
					tone: toDeltaTone(display.tone),
					prefix: display.deltaPrefix ?? '环比',
					label: display.label.replace(/^\+/, ''),
				});
			}
		}
	}

	return deltas;
};

export const buildMonthSeriesFromYoy = (periodYoy, year, field) => {
	const grid = resolvePeriodYoyGrid(periodYoy, null, 'month');
	const cells = grid?.[year] ?? [];
	if (!cells.length) return emptyMonthSeries();
	return Array.from({ length: 12 }, (_, index) => Number(cells[index]?.[field]) || 0);
};

/** 同比组合图例：对齐稿面 ¥150M · 35单 */
export const formatYoyComboLegend = (priceValues = [], countValues = []) => {
	const price = sum(priceValues) * 10000; // 入参为「万」
	const count = sum(countValues);
	let amountLabel;
	if (Math.abs(price) >= 1e8) amountLabel = `¥${(price / 1e8).toFixed(price >= 1e9 ? 0 : 1)}亿`;
	else if (Math.abs(price) >= 1e6) amountLabel = `¥${(price / 1e6).toFixed(price >= 1e7 ? 0 : 1)}M`;
	else if (Math.abs(price) >= 1e4) amountLabel = `¥${(price / 1e4).toFixed(1)}万`;
	else amountLabel = `¥${Math.round(price).toLocaleString('zh-CN')}`;
	return `${amountLabel} · ${count}单`;
};

/** 近 7 个自然日（含锚点日）本地日键；不随顶栏日期筛裁剪 */

/** 迷你柱：取有数据末月往前最多 7 根，并齐切片金额 / 标签（贴近原稿 7 柱视觉） */
export const pickRecentBarBundle = (values = [], { prices = [], labels = MONTH_AXIS } = {}) => {
	const fallback = emptyMonthSeries();
	const source = values.length ? values : fallback;
	let end = source.length - 1;
	while (end > 0 && !(Number(source[end]) > 0)) end -= 1;
	const start = Math.max(0, end - 6);
	const slicePad = (arr, pad) => {
		const sliced = (Array.isArray(arr) ? arr : []).slice(start, end + 1);
		while (sliced.length < 7) sliced.unshift(pad);
		return sliced;
	};
	const labelSource = Array.isArray(labels) && labels.length ? labels : MONTH_AXIS;
	return {
		values: slicePad(source, 0),
		prices: slicePad(prices.length ? prices : fallback, 0),
		labels: slicePad(labelSource, ''),
	};
};

export const buildMonthlySeries = (periodYoy, anchorYear) => {
	const previousYear = anchorYear - 1;
	const price = buildMonthSeriesFromYoy(periodYoy, anchorYear, 'totalPrice').map((n) => n / 10000);
	const pricePrev = buildMonthSeriesFromYoy(periodYoy, previousYear, 'totalPrice').map((n) => n / 10000);
	const count = buildMonthSeriesFromYoy(periodYoy, anchorYear, 'count');
	const countPrev = buildMonthSeriesFromYoy(periodYoy, previousYear, 'count');
	const profit = buildMonthSeriesFromYoy(periodYoy, anchorYear, 'trueProfitTotal').map((n) => n / 10000);
	const profitPrev = buildMonthSeriesFromYoy(periodYoy, previousYear, 'trueProfitTotal').map((n) => n / 10000);
	const receivable = buildMonthSeriesFromYoy(periodYoy, anchorYear, 'receivableTotal').map((n) => n / 10000);
	const rates = price.map((p, index) => (p > 0 ? profit[index] / p : 0));
	return { price, pricePrev, count, countPrev, profit, profitPrev, receivable, rates };
};

export const buildMonthlyMatrixRows = (monthly) => {
	const price = monthly.price;
	const count = monthly.count;
	const profit = monthly.profit;
	const rates = monthly.rates;
	const priceTotal = sum(price);
	const profitTotal = sum(profit);
	return [
		{
			label: '合同金额(万)',
			cells: price.map((n) => `¥${n.toFixed(2)}`),
			total: `¥${priceTotal.toFixed(2)}`,
		},
		{
			label: '订单数(个)',
			cells: count.map(String),
			total: String(sum(count)),
		},
		{
			label: '利润金额(万)',
			cells: profit.map((n) => `¥${n.toFixed(2)}`),
			total: `¥${profitTotal.toFixed(2)}`,
		},
		{
			label: '利润率',
			cells: rates.map((n) => `${(n * 100).toFixed(2)}%`),
			total: priceTotal > 0 ? `${((profitTotal / priceTotal) * 100).toFixed(2)}%` : '0.00%',
		},
	];
};

/** 总览第四行订单列表缺省页大小回退 */
/** 对齐 Falcon RecentPurchases perPage: 7 */
